<template>
  <QuickStatsPageShell
    title="Team Rankings"
    description="Team trend rankings in a tabular view with League, Market, and Timeframe filters from public Paulie data."
    :status-message="statusMessage"
  >
    <template #hero-right>
      <QuickStatsProDashboardBanner />
    </template>

    <section class="data-toolbar">
      <label class="filter-field" for="league">
        <span class="filter-label">League</span>
        <select id="league" v-model="draftLeague" class="filter-select" :disabled="loading">
          <option v-for="league in leagueOptions" :key="league" :value="league">{{ league }}</option>
        </select>
      </label>

      <label class="filter-field" for="market">
        <span class="filter-label">Market</span>
        <select id="market" v-model="draftMarket" class="filter-select" :disabled="loading">
          <option v-for="market in marketOptions" :key="market" :value="market">{{ formatMarketLabel(market) }}</option>
        </select>
      </label>

      <label class="filter-field" for="timeframe">
        <span class="filter-label">Timeframe</span>
        <select id="timeframe" v-model="draftTimeframe" class="filter-select" :disabled="loading">
          <option v-for="timeframe in timeframeOptions" :key="timeframe" :value="timeframe">
            {{ formatTimeframeLabel(timeframe, draftLeague) }}
          </option>
        </select>
      </label>

      <button
        type="button"
        class="btn btn-primary filter-action"
        :disabled="isSearchDisabled"
        @click="applyDraftFilters"
      >
        {{ loading ? 'Updating…' : 'Update Rankings' }}
      </button>
    </section>

    <p v-if="hasPendingChanges && !loading" class="pending-text">Filters changed. Click Update Rankings to refresh the table.</p>

    <QuickStatsLoadingSkeleton v-if="showInitialLoading" label="Loading team rankings" :row-count="5" />
    <div v-else-if="groupedRankings.length === 0" class="state-text">
      <strong><em>No free data available at this time. Subscribe in-app for full access.</em></strong>
    </div>

    <section v-else class="league-grid">
      <article v-for="group in groupedRankings" :key="group.league" class="card table-card">
        <header class="table-head">
          <div class="league-title-wrap">
            <QuickStatsLeagueMark :league="group.league" :logo-url="group.leagueLogo" class="league-logo" />
            <h2>{{ group.league }}</h2>
          </div>
          <p class="meta">{{ formatMarketLabel(appliedFilters.market) }} · {{ formatTimeframeLabel(appliedFilters.timeframe, appliedFilters.league) }}</p>
          <p class="updated-at">Updated {{ formatDateTime(group.latestUpdatedAt) }}</p>
        </header>

        <div class="data-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Team</th>
                <th>Record</th>
                <th>Win Rate</th>
                <th>ROI</th>
                <th class="hide-md">Pushes</th>
                <th v-if="group.showPickems" class="hide-md">Pickems</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="team in group.teams" :key="team.id">
                <td data-label="Rank">{{ team.rank }}</td>
                <td data-label="Team">
                  <router-link :to="`/quick-stats/team-rankings/${team.teamSlug}`" class="team-link">
                    <img v-if="team.logoUrl" :src="team.logoUrl" :alt="`${team.entityName} logo`" class="team-logo" loading="lazy" />
                    <span>{{ team.entityName }}</span>
                  </router-link>
                </td>
                <td data-label="Record">{{ team.record || 'N/A' }}</td>
                <td data-label="Win Rate">{{ formatPercent(team.winRate) }}</td>
                <td data-label="ROI">{{ formatPercent(team.roi) }}</td>
                <td class="hide-md" data-label="Pushes">{{ nullable(team.pushes) }}</td>
                <td v-if="group.showPickems" class="hide-md" data-label="Pickems">{{ nullable(team.pickems) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </section>

    <p class="cross-link">
      View broader patterns in
      <router-link to="/quick-stats/league-trends">League Trends</router-link>
      or today’s lines in
      <router-link to="/quick-stats/game-previews">Game Previews</router-link>.
    </p>
  </QuickStatsPageShell>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import QuickStatsPageShell from '@/components/QuickStatsPageShell.vue'
import QuickStatsLeagueMark from '@/components/QuickStatsLeagueMark.vue'
import QuickStatsLoadingSkeleton from '@/components/QuickStatsLoadingSkeleton.vue'
import QuickStatsProDashboardBanner from '@/components/QuickStatsProDashboardBanner.vue'
import { formatDateTime, formatMarketLabel, formatPercent, formatTimeframeLabel, sortTimeframes } from '@/lib/quickStats/format'
import { getTeamRankingSlice } from '@/lib/quickStats/service'

const loading = ref(false)
const rows = ref([])
const statusMessage = ref('')
const draftLeague = ref('NHL')
const draftMarket = ref('spread')
const draftTimeframe = ref('1m')
const appliedFilters = ref({
  league: 'NHL',
  market: 'spread',
  timeframe: '1m'
})

const leagueOptions = ['NFL', 'MLB', 'NBA', 'NHL', 'NCAAF', 'NCAAB']
const marketOptions = ['spread', 'moneyline', 'total']
const timeframeOptions = computed(() => {
  return sortTimeframes(['Current Season', '3m', '1m', '7d', '1d'], draftLeague.value)
})

const hasPendingChanges = computed(() => {
  return (
    draftLeague.value !== appliedFilters.value.league ||
    draftMarket.value !== appliedFilters.value.market ||
    draftTimeframe.value !== appliedFilters.value.timeframe
  )
})

const isSearchDisabled = computed(() => {
  return (
    loading.value ||
    !draftLeague.value ||
    !draftMarket.value ||
    !draftTimeframe.value ||
    !hasPendingChanges.value
  )
})

const showInitialLoading = computed(() => {
  return loading.value && rows.value.length === 0
})

const groupedRankings = computed(() => {
  const grouped = new Map()

  rows.value.forEach((row) => {
    if (!grouped.has(row.league)) {
      grouped.set(row.league, {
        league: row.league,
        leagueLogo: row.leagueLogo,
        rows: []
      })
    }

    grouped.get(row.league).rows.push(row)
  })

  return Array.from(grouped.values())
    .sort((a, b) => a.league.localeCompare(b.league))
    .map((group) => {
      const gamesPlayedByTeam = group.rows.map((row) => row.gamesPlayed).filter((value) => Number.isFinite(value))
      const averageGamesPlayed = gamesPlayedByTeam.length
        ? gamesPlayedByTeam.reduce((sum, value) => sum + value, 0) / gamesPlayedByTeam.length
        : null

      const minimumGamesPlayed = averageGamesPlayed === null ? null : Math.max(1, averageGamesPlayed - 4)

      const sortedRows = group.rows
        .filter((row) => {
          if (minimumGamesPlayed === null || row.gamesPlayed === null) return true
          return row.gamesPlayed >= minimumGamesPlayed
        })
        .slice()
        .sort((a, b) => {
          const aRate = a.winRate === null ? -1 : a.winRate
          const bRate = b.winRate === null ? -1 : b.winRate
          if (aRate !== bRate) return bRate - aRate

          const aWins = a.wins === null ? -1 : a.wins
          const bWins = b.wins === null ? -1 : b.wins
          if (aWins !== bWins) return bWins - aWins

          return a.entityName.localeCompare(b.entityName)
        })

      const latestUpdatedAt = sortedRows.reduce((latest, row) => {
        if (!row.updatedAt) return latest
        return row.updatedAt > latest ? row.updatedAt : latest
      }, '')

      return {
        league: group.league,
        leagueLogo: group.leagueLogo,
        latestUpdatedAt,
        showPickems: sortedRows.some((row) => Number(row.pickems) > 0),
        teams: sortedRows.map((row, index) => ({
          ...row,
          rank: index + 1
        }))
      }
    })
    .filter((group) => group.teams.length > 0)
})

function nullable(value) {
  return value === null || value === undefined ? 'N/A' : value
}

async function applyDraftFilters() {
  if (!draftLeague.value || !draftMarket.value || !draftTimeframe.value) return

  loading.value = true
  appliedFilters.value = {
    league: draftLeague.value,
    market: draftMarket.value,
    timeframe: draftTimeframe.value
  }

  const result = await getTeamRankingSlice(appliedFilters.value)
  rows.value = result.rows
  statusMessage.value = result.errorMessage

  if (typeof window !== 'undefined') {
    window.localStorage.setItem('teamRankingsFilters', JSON.stringify(appliedFilters.value))
  }

  loading.value = false
}

onMounted(async () => {
  let savedFilters = null

  if (typeof window !== 'undefined') {
    try {
      savedFilters = JSON.parse(window.localStorage.getItem('teamRankingsFilters') || 'null')
    } catch {
      savedFilters = null
    }
  }

  if (savedFilters?.league && leagueOptions.includes(savedFilters.league)) {
    draftLeague.value = savedFilters.league
    appliedFilters.value.league = savedFilters.league
  }

  if (savedFilters?.market && marketOptions.includes(savedFilters.market)) {
    draftMarket.value = savedFilters.market
    appliedFilters.value.market = savedFilters.market
  }

  if (savedFilters?.timeframe && timeframeOptions.value.includes(savedFilters.timeframe)) {
    draftTimeframe.value = savedFilters.timeframe
    appliedFilters.value.timeframe = savedFilters.timeframe
  }

  await applyDraftFilters()
})
</script>

<style scoped>
.state-text {
  color: var(--text-muted);
}

.pending-text {
  color: var(--text-muted);
  margin-bottom: 0.9rem;
}

.league-grid {
  display: grid;
  gap: 1rem;
}

.table-card {
  padding: 1rem;
}

.table-head {
  margin-bottom: 0.8rem;
}

.league-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta {
  font-size: 0.84rem;
  color: var(--text-muted);
  margin-top: 0.3rem;
}

.updated-at {
  margin-top: 0.2rem;
  color: var(--text-muted);
  font-size: 0.82rem;
}

.team-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: var(--text-primary);
  font-weight: 800;
  text-decoration: none;
}

.team-logo,
.league-logo {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.cross-link {
  margin-top: 1rem;
}

.cross-link a {
  color: var(--accent);
  text-decoration: none;
}
</style>
